import React, { ReactElement, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useCoCurricularApi } from 'services/api/hooks';
import { colorPrimaryNavy } from 'customColors';
import { StatsSummary } from './StatsSummary';
import { CompetenciesSection } from './CompetenciesSection';
import { LeadershipPositionsSection } from './LeadershipPositionsSection';
import { AllEntriesTable } from './AllEntriesTable';

interface Competency {
  label: string;
  hours: string;
  events: number;
  progress: number;
  color: string;
}

export const CoCurricularTab = ({
  variant = 'student',
}: {
  variant?: 'student' | 'admin';
}): ReactElement => {
  const coCurricularApi = useCoCurricularApi();
  const [learningOutcomes, setLearningOutcomes] = useState<Competency[]>([]);
  const [loading, setLoading] = useState(true);
  const [naceCompetencies, setNaceCompetencies] = useState<Competency[]>([]);
  const [naceLoading, setNaceLoading] = useState(true);
  const [totalEvents, setTotalEvents] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await coCurricularApi.getLearningOutcomeHours();

      if (result.success) {
        const competencies = result.value.learningOutcomes.map(lo => ({
          label: lo.archived
            ? `${lo.learningOutcomeTitle} (Archived)`
            : lo.learningOutcomeTitle,
          hours: `${lo.totalHours.toFixed(1)} h`,
          events: lo.eventCount,
          progress: lo.totalHoursAllTime > 0
            ? Math.round((lo.totalHours / lo.totalHoursAllTime) * 100)
            : 0,
          color: colorPrimaryNavy,
        }));

        setLearningOutcomes(competencies);
      }
      setLoading(false);
    };

    fetchData();
  }, [coCurricularApi]);

  useEffect(() => {
    const fetchNaceData = async () => {
      setNaceLoading(true);
      const result = await coCurricularApi.getNaceCompetencyHours();

      if (result.success) {
        const competencies = [...result.value.naceCompetencies]
          .sort((a, b) => b.totalHours - a.totalHours)
          .map(nc => ({
            label: nc.naceCompetency,
            hours: `${nc.totalHours.toFixed(1)} h`,
            events: nc.eventCount,
            progress: nc.totalHoursAllTime > 0
              ? Math.round((nc.totalHours / nc.totalHoursAllTime) * 100)
              : 0,
            color: colorPrimaryNavy,
          }));

        setNaceCompetencies(competencies);
      }
      setNaceLoading(false);
    };

    fetchNaceData();
  }, [coCurricularApi]);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      const result = await coCurricularApi.getSummary();

      if (result.success)
        setTotalEvents(result.value.totalEvents);

      setSummaryLoading(false);
    };

    fetchSummary();
  }, [coCurricularApi]);

  return (
    <Box sx={{ paddingTop: '24px' }}>
      <StatsSummary />
      <CompetenciesSection
        title="Career-readiness competencies (NACE 8)"
        subtitle="Hours and events tagged to each competency. Ordered by hours."
        competencies={naceCompetencies}
        loading={naceLoading}
        columnCount={variant === 'admin' ? 4 : 2}
      />
      {!summaryLoading && totalEvents > 0 && (
        <CompetenciesSection
          title="University Learning Outcomes"
          subtitle="Hours and events tagged to each competency."
          competencies={learningOutcomes}
          loading={loading}
        />
      )}
      <LeadershipPositionsSection variant={variant} />
      <AllEntriesTable variant={variant} />
    </Box>
  );
};
